// Hand-curated set of scenic / pleasant / quiet routes used as SEO landing
// pages. Each entry must be a genuinely strong, differentiated route — we
// are deliberately NOT auto-generating these. Adding a new entry here
// automatically adds it to the hub page and (after re-running the sitemap
// generator below) the public sitemap.

import type { LngLat } from "@/utils/polyline";

export type RouteMode = "driving" | "walking";
export type RouteRegion =
  | "Westchester County"
  | "New York City"
  | "San Francisco Bay Area"
  | "Los Angeles"
  | "San Diego"
  | "Seattle"
  | "Portland"
  | "Boston"
  | "Chicago"
  | "Washington DC"
  | "Denver"
  | "Austin"
  | "Miami"
  | "London"
  | "Paris"
  | "Sydney"
  | "Vancouver"
  | "Melbourne";

export interface ScenicRoute {
  /** URL slug — used at /routes/{slug}. Keep it search-matching and stable. */
  slug: string;
  /** H1 + page <title> base. */
  title: string;
  /** Meta description. ~150 chars, human, not stuffed. */
  metaDescription: string;
  /** 1–2 sentence intro shown directly under the H1. */
  intro: string;
  region: RouteRegion;
  mode: RouteMode;
  /** Approximate trip time (e.g. "30 min", "1 hr 10 min"). */
  time: string;
  /** Approximate distance (e.g. "12 miles", "1.8 miles"). */
  distance: string;
  /** "High" | "Very High" — surfaced as the pleasantness indicator. */
  pleasantness: "High" | "Very High";
  /** Why this route is pleasant — 3–5 specific reasons (trees, water, etc.). */
  whyPleasant: string[];
  /** What you'll see — concrete, visual landmarks/details. */
  whatYoullSee: string[];
  /** Best time to go — simple, useful guidance. */
  bestTime: string;
  /** Approximate start point — used to deep-link into the app. */
  start: { name: string; coords: LngLat };
  /** Approximate end point — used to deep-link into the app. */
  end: { name: string; coords: LngLat };
}

/**
 * Build an "Open in Pleasant Routes" deep link that the app's Index page
 * understands (see Index.tsx — /?start=lng,lat|name&end=…&mode=…).
 */
export const buildOpenInAppHref = (r: ScenicRoute): string => {
  const s = `${r.start.coords[0]},${r.start.coords[1]}|${r.start.name}`;
  const e = `${r.end.coords[0]},${r.end.coords[1]}|${r.end.name}`;
  const mode = r.mode === "walking" ? "walking" : "driving";
  return `/app?start=${encodeURIComponent(s)}&end=${encodeURIComponent(e)}&mode=${mode}`;
};

export const SCENIC_ROUTES: ScenicRoute[] = [
  // ── Westchester County ────────────────────────────────────────────────
  {
    slug: "scenic-drive-bronx-river-parkway",
    title: "Scenic Drive on the Bronx River Parkway",
    metaDescription:
      "A tree-lined scenic drive in Westchester that follows the Bronx River through old stone bridges, woodland and the Westchester County parks system.",
    intro:
      "The Bronx River Parkway is one of America's first true parkways — a slow, curving drive built specifically to feel like a park, not a highway.",
    region: "Westchester County",
    mode: "driving",
    time: "35 min",
    distance: "13 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Continuous tree canopy for nearly the entire route",
      "Follows the Bronx River — water on one side, woods on the other",
      "Original 1920s stone overpasses and arched bridges",
      "Sundays it closes to cars in summer for Bicycle Sundays",
      "No commercial signage, no billboards, no strip malls",
    ],
    whatYoullSee: [
      "Kensico Dam Plaza at the northern end",
      "Bronxville's leafy stretch through residential parkland",
      "The river meandering beside the road through Scarsdale",
      "Old stone bridges arching overhead",
    ],
    bestTime: "Weekday mornings before 8am, or any Sunday in late spring when the leaves have just come in.",
    start: { name: "Kensico Dam Plaza, Valhalla", coords: [-73.7657, 41.0734] },
    end: { name: "Bronxville", coords: [-73.8338, 40.9387] },
  },
  {
    slug: "scenic-drive-hudson-river-westchester",
    title: "Scenic Drive Along the Hudson River in Westchester",
    metaDescription:
      "Drive Route 9 from Hastings-on-Hudson up through Sleepy Hollow with wide Hudson River views, river towns, and Rockefeller estate land along the way.",
    intro:
      "This stretch of Route 9 hugs the eastern bank of the Hudson, threading through small river towns with constant water views to your left.",
    region: "Westchester County",
    mode: "driving",
    time: "45 min",
    distance: "15 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Hudson River visible for most of the drive",
      "Slow speeds through historic main streets, not highway pace",
      "Crosses the Old Croton Aqueduct trail several times",
      "Mature trees frame nearly every block",
    ],
    whatYoullSee: [
      "Hastings-on-Hudson's restored riverfront",
      "Lyndhurst Mansion's Gothic gates",
      "The Tappan Zee / Mario Cuomo Bridge from the bluffs above Tarrytown",
      "Sleepy Hollow's old churchyard and lighthouse",
    ],
    bestTime: "Late afternoon in October — the foliage on the Palisades across the river lights up.",
    start: { name: "Hastings-on-Hudson", coords: [-73.8790, 40.9923] },
    end: { name: "Sleepy Hollow", coords: [-73.8587, 41.0859] },
  },
  {
    slug: "pleasant-drive-rockefeller-state-park",
    title: "Pleasant Drive Through Rockefeller State Park Preserve",
    metaDescription:
      "A quiet country drive through Pocantico Hills past stone walls, horse farms and the carriage roads of the Rockefeller State Park Preserve.",
    intro:
      "Bedford Road through Pocantico Hills is country driving fifteen minutes from the Tappan Zee — stone walls, fields, and very little traffic.",
    region: "Westchester County",
    mode: "driving",
    time: "25 min",
    distance: "8 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Almost no commercial development along the route",
      "Dry-stack stone walls line both sides for miles",
      "Borders 1,600+ acres of preserved Rockefeller land",
      "Light traffic even on weekends",
    ],
    whatYoullSee: [
      "Stone Barns Center for Food and Agriculture",
      "Union Church of Pocantico Hills (Chagall and Matisse windows)",
      "Open hayfields with the Hudson Highlands behind them",
      "Trailheads for the carriage roads",
    ],
    bestTime: "Saturday morning, year-round. Best in October for foliage, May for wildflowers.",
    start: { name: "Sleepy Hollow", coords: [-73.8587, 41.0859] },
    end: { name: "Pocantico Hills", coords: [-73.8378, 41.1011] },
  },
  {
    slug: "quiet-walk-untermyer-gardens-yonkers",
    title: "Quiet Walk Through Untermyer Gardens in Yonkers",
    metaDescription:
      "A quiet walking route through Untermyer Park's restored Persian walled garden and out to the Hudson River overlooks above Yonkers.",
    intro:
      "Untermyer is the most extraordinary public garden almost no one outside Westchester has heard of — a restored Persian-style walled garden on a Hudson bluff.",
    region: "Westchester County",
    mode: "walking",
    time: "1 hr",
    distance: "1.5 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Almost completely closed off from street noise",
      "Mature trees shade nearly every path",
      "Wide Hudson River views from the Vista path",
      "Very few visitors midweek",
    ],
    whatYoullSee: [
      "The Walled Garden's reflecting pools and mosaic",
      "Greek-style amphitheater and sphinxes",
      "The Vista — a long stone staircase descending toward the river",
      "Old-growth weeping beech tree near the entrance",
    ],
    bestTime: "Weekday mornings in late spring, when the wisteria is in bloom.",
    start: { name: "Untermyer Park entrance, Yonkers", coords: [-73.8979, 40.9722] },
    end: { name: "Hudson River Overlook", coords: [-73.9001, 40.9714] },
  },
  {
    slug: "scenic-drive-saw-mill-river-parkway",
    title: "Scenic Drive on the Saw Mill River Parkway",
    metaDescription:
      "An older Westchester parkway that runs through dense woods and along the Saw Mill River — slower, leafier and more pleasant than the highways either side of it.",
    intro:
      "The Saw Mill is the parkway locals use when they're not in a rush — narrow, curving, and deeply wooded for most of its length.",
    region: "Westchester County",
    mode: "driving",
    time: "30 min",
    distance: "12 miles",
    pleasantness: "High",
    whyPleasant: [
      "Continuous forest cover from Hawthorne south",
      "No trucks allowed — passenger cars only",
      "Original parkway-era stone overpasses",
      "Glimpses of the Saw Mill River alongside the road",
    ],
    whatYoullSee: [
      "Wooded ravines and old retaining walls",
      "Crossings of the North County Trailway",
      "The river running parallel near Elmsford",
    ],
    bestTime: "Off-peak weekdays — avoid the 5–7pm commuter window.",
    start: { name: "Hawthorne, NY", coords: [-73.7949, 41.1076] },
    end: { name: "Yonkers", coords: [-73.8987, 40.9312] },
  },
  {
    slug: "quiet-walk-old-croton-aqueduct",
    title: "Quiet Walk on the Old Croton Aqueduct Trail",
    metaDescription:
      "A quiet, mostly-flat walking trail that runs for miles along the historic Croton Aqueduct through the Hudson River villages of Westchester.",
    intro:
      "The Aqueduct trail is a long ribbon of dirt path on top of a 19th-century water tunnel — quiet, shaded, and nearly flat for as far as you want to walk.",
    region: "Westchester County",
    mode: "walking",
    time: "1 hr",
    distance: "2.5 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Almost entirely off-road and traffic-free",
      "Tree canopy for nearly the full length",
      "Gentle gradient — easy walking for any fitness level",
      "Passes through residential village backs, not commercial strips",
    ],
    whatYoullSee: [
      "Original aqueduct ventilator stones",
      "Backyards, gardens and the occasional Hudson River view through the trees",
      "Old stone bridges and the Ossining double-arch",
    ],
    bestTime: "Early morning year-round. Magical after a light snow.",
    start: { name: "Irvington Aqueduct Trailhead", coords: [-73.8676, 41.0376] },
    end: { name: "Tarrytown Aqueduct Trailhead", coords: [-73.8587, 41.0762] },
  },
  {
    slug: "pleasant-drive-tarrytown-sleepy-hollow",
    title: "Pleasant Drive Through Tarrytown and Sleepy Hollow",
    metaDescription:
      "A short, characterful drive through the historic main streets of Tarrytown and Sleepy Hollow with Hudson River views and 19th-century architecture.",
    intro:
      "Two small Hudson River villages connected by a slow, walkable main street — Broadway here is everything Broadway in Manhattan is not.",
    region: "Westchester County",
    mode: "driving",
    time: "20 min",
    distance: "4 miles",
    pleasantness: "High",
    whyPleasant: [
      "Slow village speeds, not highway driving",
      "Independent shops and restored 19th-century storefronts",
      "Hudson River appears at every cross street",
      "Rich tree cover through residential blocks",
    ],
    whatYoullSee: [
      "Tarrytown Music Hall (1885)",
      "Patriots Park and the Captor's Monument",
      "The Old Dutch Church and burying ground",
      "Philipsburg Manor",
    ],
    bestTime: "Weekday afternoon, or weekends in October during Sleepy Hollow's Halloween events.",
    start: { name: "Tarrytown Main Street", coords: [-73.8587, 41.0762] },
    end: { name: "Philipsburg Manor, Sleepy Hollow", coords: [-73.8627, 41.0936] },
  },

  // ── New York City ─────────────────────────────────────────────────────
  {
    slug: "quiet-walk-central-park",
    title: "Quiet Walk Through Central Park",
    metaDescription:
      "A quiet walking route through Central Park's wooded northern half — the Ramble, the Pool and the Ravine — avoiding the tourist crowds south of 72nd.",
    intro:
      "Central Park's quiet half is the part most visitors never see — a genuinely wooded landscape that feels nothing like the city around it.",
    region: "New York City",
    mode: "walking",
    time: "55 min",
    distance: "2.2 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Far fewer people than the southern park",
      "Real forest in the Ramble and the Ravine — not just lawn",
      "Constant water — the Lake, the Pool, the Loch",
      "Almost no traffic noise once you're past 79th Street",
    ],
    whatYoullSee: [
      "The Loch waterfall in the Ravine",
      "The Pool's weeping willows",
      "Balcony Bridge and Bow Bridge views over the Lake",
      "The North Woods' rocky outcrops",
    ],
    bestTime: "Weekday mornings before 10am. Avoid summer Saturdays after noon.",
    start: { name: "Central Park West & 100th St", coords: [-73.9594, 40.7956] },
    end: { name: "Bow Bridge", coords: [-73.9722, 40.7758] },
  },
  {
    slug: "scenic-drive-fdr-east-river",
    title: "Scenic Drive on the FDR Along the East River",
    metaDescription:
      "Drive the FDR Drive northbound at off-peak hours for unobstructed views of the East River, the bridges, and the Manhattan and Brooklyn waterfronts.",
    intro:
      "The FDR isn't pretty in the parkway sense — but at the right hour it offers some of the best moving views in the city.",
    region: "New York City",
    mode: "driving",
    time: "20 min",
    distance: "9 miles",
    pleasantness: "High",
    whyPleasant: [
      "Continuous East River views the entire way",
      "Brooklyn, Manhattan, Williamsburg and Queensboro bridges from below",
      "No commercial billboards along the route",
      "At night, the bridge lights and skyline are the show",
    ],
    whatYoullSee: [
      "Brooklyn Bridge from underneath",
      "South Street Seaport and the East River piers",
      "United Nations headquarters",
      "Roosevelt Island and the Queensboro Bridge",
    ],
    bestTime: "Sunday morning before 9am, or any weeknight after 9pm for the lit skyline.",
    start: { name: "FDR Drive at Battery Park", coords: [-74.0145, 40.7022] },
    end: { name: "FDR Drive at 96th St", coords: [-73.9430, 40.7820] },
  },
  {
    slug: "pleasant-walk-brooklyn-bridge-park",
    title: "Pleasant Walk Along Brooklyn Bridge Park",
    metaDescription:
      "Walk the Brooklyn Bridge Park waterfront from DUMBO to Atlantic Avenue — gardens, piers and unbroken views of the Lower Manhattan skyline.",
    intro:
      "A 1.3-mile waterfront park built on top of the old Brooklyn shipping piers, with the best skyline view in the five boroughs.",
    region: "New York City",
    mode: "walking",
    time: "45 min",
    distance: "1.6 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Continuous Lower Manhattan skyline view the whole way",
      "Native plantings and salt marsh on Pier 1 and Pier 6",
      "Wide separated paths — pedestrians and cyclists don't conflict",
      "Constant breeze off the harbor",
    ],
    whatYoullSee: [
      "Brooklyn Bridge from directly underneath",
      "Jane's Carousel (the restored 1922 carousel in glass)",
      "Pier 2 sports courts and Pier 5 lawns",
      "Statue of Liberty in the distance from Pier 6",
    ],
    bestTime: "Late afternoon — golden hour on the Manhattan skyline is the moment.",
    start: { name: "DUMBO, Brooklyn", coords: [-73.9892, 40.7033] },
    end: { name: "Pier 6, Brooklyn Heights", coords: [-74.0019, 40.6927] },
  },
  {
    slug: "quiet-walk-prospect-park",
    title: "Quiet Walk Through Prospect Park",
    metaDescription:
      "A quiet walking loop through Prospect Park's Long Meadow, Ravine and Lullwater — the Olmsted-designed Brooklyn park most New Yorkers prefer to Central Park.",
    intro:
      "Olmsted considered Prospect Park his masterpiece — and walking the Ravine and the Lullwater early in the morning, it's easy to see why.",
    region: "New York City",
    mode: "walking",
    time: "1 hr 10 min",
    distance: "3 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Real forest in the Ravine — the only one in Brooklyn",
      "Wide, tree-shaded walking paths separated from cars",
      "Constant water — the Lullwater, the Lake, the falls",
      "Significantly less crowded than Central Park",
    ],
    whatYoullSee: [
      "Long Meadow at sunrise",
      "The Boathouse and Lullwater bridge",
      "Audubon Center wildlife refuge",
      "The Ravine's cascades and old oaks",
    ],
    bestTime: "Weekday mornings before 9am. The Long Meadow at first light is the best moment.",
    start: { name: "Grand Army Plaza, Brooklyn", coords: [-73.9701, 40.6739] },
    end: { name: "Prospect Park Boathouse", coords: [-73.9655, 40.6643] },
  },
  {
    slug: "scenic-drive-belt-parkway",
    title: "Scenic Drive on the Belt Parkway",
    metaDescription:
      "Drive the Belt Parkway along the Brooklyn and Queens shoreline for nonstop views of Jamaica Bay, the Verrazzano Bridge and the Atlantic.",
    intro:
      "The Belt is a working highway that happens to follow the entire south shore of Brooklyn and Queens — water on one side for nearly the whole ride.",
    region: "New York City",
    mode: "driving",
    time: "40 min",
    distance: "20 miles",
    pleasantness: "High",
    whyPleasant: [
      "Water visible for nearly the entire route",
      "Verrazzano-Narrows Bridge appears as you round Bay Ridge",
      "No trucks allowed — cars only",
      "Parkway-era stone overpasses and bike path alongside",
    ],
    whatYoullSee: [
      "The Verrazzano-Narrows Bridge framed against the harbor",
      "Coney Island parachute jump",
      "Jamaica Bay Wildlife Refuge",
      "JFK Airport runways from above the water",
    ],
    bestTime: "Sunday morning before 10am — it's a different parkway without traffic.",
    start: { name: "Owl's Head Park, Bay Ridge", coords: [-74.0345, 40.6440] },
    end: { name: "Howard Beach, Queens", coords: [-73.8462, 40.6571] },
  },
  {
    slug: "pleasant-walk-the-high-line",
    title: "Pleasant Walk on the High Line",
    metaDescription:
      "Walk the full length of the High Line, Manhattan's elevated park, from Gansevoort Street up through Hudson Yards — gardens, river views and quiet above the traffic.",
    intro:
      "An elevated 1.45-mile park built on a disused freight rail line — and one of the few places in Manhattan where you walk above the traffic, not next to it.",
    region: "New York City",
    mode: "walking",
    time: "50 min",
    distance: "1.5 miles",
    pleasantness: "High",
    whyPleasant: [
      "Entirely separated from street traffic",
      "Native and prairie plantings the whole length",
      "Hudson River views on the western stretches",
      "Frequent benches and viewing decks",
    ],
    whatYoullSee: [
      "The original rail tracks running through the plantings",
      "The 10th Avenue overlook seating amphitheater",
      "Hudson Yards Vessel and the Edge",
      "Chelsea Market and the Whitney from above",
    ],
    bestTime: "Weekday mornings or after 7pm in summer. Avoid Saturday afternoons.",
    start: { name: "High Line — Gansevoort St", coords: [-74.0086, 40.7398] },
    end: { name: "High Line — Hudson Yards", coords: [-74.0024, 40.7547] },
  },
  {
    slug: "quiet-walk-fort-tryon-park",
    title: "Quiet Walk Through Fort Tryon Park",
    metaDescription:
      "A quiet walking route through Fort Tryon Park to The Cloisters — wooded paths, Hudson River bluffs and Manhattan's most-overlooked park.",
    intro:
      "Fort Tryon sits on a high bluff at Manhattan's northern end — wooded, almost empty, with cliff-edge views of the Hudson and the Palisades.",
    region: "New York City",
    mode: "walking",
    time: "55 min",
    distance: "1.8 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Sits 200 feet above the Hudson on a rocky bluff",
      "Mature trees and rock outcrops, not landscaped lawn",
      "Heather Garden — one of the only public heather gardens in the US",
      "Genuinely uncrowded, even on weekends",
    ],
    whatYoullSee: [
      "The Cloisters museum at the high point",
      "The Heather Garden in bloom March–November",
      "Hudson River views across to the Palisades",
      "Linden Terrace's stonework",
    ],
    bestTime: "Weekday mornings, or fall weekends when the Palisades are in foliage.",
    start: { name: "Fort Tryon Park entrance, 190th St", coords: [-73.9347, 40.8597] },
    end: { name: "The Cloisters", coords: [-73.9316, 40.8649] },
  },

  // ── San Francisco Bay Area ────────────────────────────────────────────
  {
    slug: "scenic-drive-pacific-coast-highway-san-francisco-half-moon-bay",
    title: "Scenic Drive on the Pacific Coast Highway: San Francisco to Half Moon Bay",
    metaDescription:
      "Drive Highway 1 from San Francisco down the coast to Half Moon Bay — Pacifica's surf, the Devil's Slide cliffs, and open ocean almost the whole way.",
    intro:
      "This is the quintessential Bay Area coast drive: Highway 1 leaving the city, hugging the bluffs through Pacifica, curving around Devil's Slide, and rolling down to the beaches of Half Moon Bay.",
    region: "San Francisco Bay Area",
    mode: "driving",
    time: "50 min",
    distance: "28 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Open Pacific Ocean on your right for most of the drive",
      "The Devil's Slide coastal cliffs, with a car-free tunnel bypass and trail",
      "Far less traffic and signage than the inland 280 route",
      "Pullouts and overlooks the whole way down for photos",
      "Salt air, fog burning off the headlands, and surf breaks at Pacifica",
    ],
    whatYoullSee: [
      "Pacifica State Beach and the Pacifica Pier",
      "The dramatic Devil's Slide bluffs and Gray Whale Cove",
      "Montara Lighthouse on its headland",
      "The wide crescent of Half Moon Bay's beaches at the end",
    ],
    bestTime: "Late afternoon on a clear day, when the light goes gold over the water — or any time the coastal fog has lifted.",
    start: { name: "Great Highway, San Francisco", coords: [-122.5096, 37.7594] },
    end: { name: "Half Moon Bay", coords: [-122.4286, 37.4636] },
  },
  {
    slug: "scenic-drive-golden-gate-bridge-marin-headlands",
    title: "Scenic Drive Across the Golden Gate Bridge to the Marin Headlands",
    metaDescription:
      "Cross the Golden Gate Bridge and climb into the Marin Headlands for the classic postcard view back at the bridge, the bay, and the San Francisco skyline.",
    intro:
      "Short, iconic, and almost absurdly scenic: over the Golden Gate Bridge, then up the winding road into the Marin Headlands for the view everyone comes for.",
    region: "San Francisco Bay Area",
    mode: "driving",
    time: "25 min",
    distance: "6 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Crosses the Golden Gate Bridge itself",
      "Climbs Conzelman Road along the headland cliffs",
      "The single best view back at the bridge and the city",
      "Open coastal grassland and old military batteries, no development",
      "Ocean on one side, bay on the other",
    ],
    whatYoullSee: [
      "The Golden Gate Bridge towers up close",
      "Hawk Hill and the Conzelman Road overlooks",
      "Point Bonita Lighthouse out on the point",
      "The SF skyline framed across the bay",
    ],
    bestTime: "Early morning before the fog rolls back in, or just before sunset for the warm light on the bridge.",
    start: { name: "Golden Gate Bridge Welcome Center, SF", coords: [-122.4783, 37.8079] },
    end: { name: "Hawk Hill, Marin Headlands", coords: [-122.4990, 37.8267] },
  },
  {
    slug: "scenic-walk-lands-end-san-francisco",
    title: "Scenic Coastal Walk at Lands End, San Francisco",
    metaDescription:
      "Walk the Lands End Trail in San Francisco — cypress groves, ruined Sutro Baths, shipwreck coves, and clear views of the Golden Gate Bridge across the water.",
    intro:
      "Lands End is the wildest edge of San Francisco — a cliffside trail through cypress and rock above the surf, with the Golden Gate Bridge appearing through the trees.",
    region: "San Francisco Bay Area",
    mode: "walking",
    time: "1 hr",
    distance: "3.5 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Cliffside coastal trail far from any traffic",
      "Cypress and Monterey pine groves the whole way",
      "Constant ocean sound and sea air",
      "Views of the Golden Gate Bridge across the channel",
      "The ruins of the Sutro Baths at the trailhead",
    ],
    whatYoullSee: [
      "The Sutro Baths ruins and sea caves",
      "The Lands End Labyrinth out on the point",
      "Mile Rock Beach down the cliff stairs",
      "The Golden Gate Bridge framed through the cypress",
    ],
    bestTime: "A clear morning before the afternoon wind picks up; weekdays are quietest.",
    start: { name: "Lands End Lookout / Sutro Baths, SF", coords: [-122.5113, 37.7800] },
    end: { name: "Eagle's Point, Lands End", coords: [-122.4960, 37.7818] },
  },
  {
    slug: "scenic-drive-skyline-boulevard-santa-cruz-mountains",
    title: "Scenic Drive on Skyline Boulevard Through the Santa Cruz Mountains",
    metaDescription:
      "Drive Skyline Boulevard (Highway 35) along the ridge of the Santa Cruz Mountains — redwoods, open-space preserves, and views over both the bay and the ocean.",
    intro:
      "Skyline Boulevard runs the spine of the Santa Cruz Mountains, a quiet two-lane ridge road with redwood groves on one side and long views to the bay or the sea on the other.",
    region: "San Francisco Bay Area",
    mode: "driving",
    time: "40 min",
    distance: "18 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Runs the forested ridgeline, cool and shaded",
      "Redwood and oak groves with frequent open-space preserves",
      "Long views east over the bay and west toward the ocean",
      "Very little traffic and almost no commercial development",
      "A string of trailheads and overlooks to stop at",
    ],
    whatYoullSee: [
      "Redwood groves at Purisima Creek and El Corte de Madera",
      "Windy Hill and Russian Ridge open-space preserves",
      "The bay laid out below to the east on clear days",
      "Alice's Restaurant at the Sky Londa crossroads",
    ],
    bestTime: "A clear day after the fog clears the ridge; autumn mornings are especially crisp.",
    start: { name: "Skyline Blvd at Highway 92", coords: [-122.3556, 37.5106] },
    end: { name: "Sky Londa (Skyline at Highway 84)", coords: [-122.2913, 37.3884] },
  },
  {
    slug: "pleasant-walk-embarcadero-san-francisco",
    title: "Pleasant Waterfront Walk Along the Embarcadero, San Francisco",
    metaDescription:
      "Walk San Francisco's Embarcadero from the Ferry Building to Fisherman's Wharf — bay views, the Bay Bridge lights, piers, and the open waterfront promenade.",
    intro:
      "The Embarcadero is San Francisco's flat, breezy waterfront promenade — a straight, easy walk along the bay from the Ferry Building up to the wharf, water on one side the whole way.",
    region: "San Francisco Bay Area",
    mode: "walking",
    time: "45 min",
    distance: "2 miles",
    pleasantness: "High",
    whyPleasant: [
      "Open San Francisco Bay on your right the entire way",
      "Flat, wide, car-separated promenade",
      "The Bay Bridge and its light installation overhead at the start",
      "Historic piers, palms, and the Ferry Building marketplace",
      "Sea lions, ferries, and constant water activity",
    ],
    whatYoullSee: [
      "The Ferry Building and its clock tower",
      "The Bay Bridge and Cupid's Span sculpture",
      "Pier 7's old wooden fishing pier",
      "Sea lions hauled out at Pier 39",
    ],
    bestTime: "Morning for calm water and light crowds, or evening when the Bay Bridge lights come on.",
    start: { name: "Ferry Building, San Francisco", coords: [-122.3933, 37.7955] },
    end: { name: "Fisherman's Wharf / Pier 39", coords: [-122.4106, 37.8087] },
  },

  // ── Los Angeles ───────────────────────────────────────────────────────
  {
    slug: "scenic-drive-pacific-coast-highway-malibu",
    title: "Scenic Drive on the Pacific Coast Highway Through Malibu",
    metaDescription:
      "Drive the Pacific Coast Highway from Santa Monica through Malibu — open ocean on one side, the Santa Monica Mountains on the other, and beaches the whole way.",
    intro:
      "PCH through Malibu is the Los Angeles coast drive: the Santa Monica Mountains rising on your right, the open Pacific on your left, and one famous beach after another.",
    region: "Los Angeles",
    mode: "driving",
    time: "45 min",
    distance: "21 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Open ocean views for nearly the entire drive",
      "Santa Monica Mountains rising straight up on the inland side",
      "A string of state beaches to pull off at",
      "Sea breeze and surf the whole way",
      "Far calmer than the parallel inland freeways",
    ],
    whatYoullSee: [
      "Santa Monica Pier at the start",
      "Surfers at Surfrider Beach by the Malibu Pier",
      "The bluffs and coves around Point Dume",
      "Zuma Beach's long open sand",
    ],
    bestTime: "Late afternoon toward sunset, or a clear weekday morning before the beach traffic.",
    start: { name: "Santa Monica Pier", coords: [-118.4977, 34.0094] },
    end: { name: "Point Dume, Malibu", coords: [-118.8059, 34.0008] },
  },
  {
    slug: "scenic-drive-mulholland-drive-los-angeles",
    title: "Scenic Drive on Mulholland Drive, Los Angeles",
    metaDescription:
      "Drive Mulholland Drive along the crest of the Santa Monica Mountains — overlooks of the LA basin on one side and the San Fernando Valley on the other.",
    intro:
      "Mulholland runs the ridge that splits Los Angeles in two, a winding road with overlooks that drop away to the city on one side and the Valley on the other.",
    region: "Los Angeles",
    mode: "driving",
    time: "35 min",
    distance: "12 miles",
    pleasantness: "High",
    whyPleasant: [
      "Follows the very crest of the Santa Monica Mountains",
      "Overlooks of the LA basin and the San Fernando Valley",
      "Chaparral hillsides instead of city blocks",
      "A slow, curving ridge road built for the view",
      "City lights at night from the official overlooks",
    ],
    whatYoullSee: [
      "The Hollywood Sign and Griffith Observatory across the canyon",
      "Universal City and the Valley spread out to the north",
      "The Stone Canyon and Hollywood reservoirs below",
      "Downtown LA's towers on a clear day",
    ],
    bestTime: "Just before sunset for the light on the basin, or after dark for the city lights.",
    start: { name: "Mulholland at Cahuenga Pass", coords: [-118.3603, 34.1340] },
    end: { name: "Mulholland Scenic Overlook", coords: [-118.4438, 34.1244] },
  },
  {
    slug: "scenic-drive-angeles-crest-highway",
    title: "Scenic Drive on the Angeles Crest Highway",
    metaDescription:
      "Drive the Angeles Crest Highway out of La Cañada into the San Gabriel Mountains — pine forest, switchbacks, and overlooks high above the Los Angeles basin.",
    intro:
      "The Angeles Crest climbs straight out of the city into real mountains — switchbacks through pine forest, with the whole LA basin falling away below you.",
    region: "Los Angeles",
    mode: "driving",
    time: "40 min",
    distance: "15 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Climbs from the suburbs into the San Gabriel high country",
      "Pine and cedar forest, cool air, real elevation",
      "Switchback overlooks above the LA basin",
      "Quiet two-lane mountain road inside a National Forest",
      "Snow-dusted peaks in winter, wildflowers in spring",
    ],
    whatYoullSee: [
      "The LA basin spread out far below from the lower overlooks",
      "Pine forest closing in as you climb",
      "Clear Creek and the Arroyo Seco canyon",
      "Mount Wilson and its observatory off to the side",
    ],
    bestTime: "A clear morning after a storm, when the air is washed clean and you can see to the ocean.",
    start: { name: "La Cañada Flintridge (Hwy 2 start)", coords: [-118.1973, 34.2086] },
    end: { name: "Red Box Junction", coords: [-118.1133, 34.2585] },
  },

  // ── San Diego ─────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-coast-highway-101-la-jolla-del-mar",
    title: "Scenic Drive on Coast Highway 101: La Jolla to Del Mar",
    metaDescription:
      "Drive old Coast Highway 101 north from La Jolla through Torrey Pines and Del Mar — ocean bluffs, beach towns and the cliffs of Torrey Pines State Reserve.",
    intro:
      "The old coast highway north of La Jolla is San Diego at its best — beach towns strung along the bluffs, with the Pacific open beside you and Torrey Pines rising ahead.",
    region: "San Diego",
    mode: "driving",
    time: "30 min",
    distance: "11 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Hugs the ocean bluffs the whole way",
      "Threads the laid-back beach towns instead of the freeway",
      "Climbs the cliffs through Torrey Pines State Reserve",
      "Hang gliders, surfers and tide pools along the route",
      "Easy pullouts above the beaches",
    ],
    whatYoullSee: [
      "La Jolla Cove and the sea lions",
      "The Torrey Pines cliffs and glider port",
      "Los Peñasquitos lagoon and the long Torrey Pines beach",
      "Del Mar's bluff-top village and racetrack",
    ],
    bestTime: "Late afternoon for the light on the cliffs; weekday mornings are quietest.",
    start: { name: "La Jolla Cove", coords: [-117.2720, 32.8508] },
    end: { name: "Del Mar", coords: [-117.2653, 32.9595] },
  },
  {
    slug: "scenic-drive-sunset-cliffs-san-diego",
    title: "Scenic Drive on Sunset Cliffs Boulevard, San Diego",
    metaDescription:
      "Drive Sunset Cliffs Boulevard along the sandstone sea cliffs of Point Loma in San Diego — open Pacific views, surf breaks, and the best sunset spot in the city.",
    intro:
      "Sunset Cliffs Boulevard runs right along the edge of Point Loma's sandstone bluffs, the open Pacific dropping away just past the guardrail.",
    region: "San Diego",
    mode: "driving",
    time: "15 min",
    distance: "3 miles",
    pleasantness: "High",
    whyPleasant: [
      "Runs directly along the sea cliffs",
      "Unbroken open-ocean horizon",
      "Surf breaking on the rocks below",
      "The city's classic sunset-watching stretch",
      "Small clifftop park with trails to wander",
    ],
    whatYoullSee: [
      "The sandstone arches and sea caves of the cliffs",
      "Surfers in the breaks below",
      "Sunset Cliffs Natural Park",
      "Open Pacific all the way to the horizon",
    ],
    bestTime: "Sunset, unsurprisingly — arrive 30 minutes early for a clifftop spot.",
    start: { name: "Ocean Beach Pier", coords: [-117.2530, 32.7497] },
    end: { name: "Sunset Cliffs Natural Park", coords: [-117.2547, 32.7188] },
  },
  {
    slug: "scenic-drive-point-loma-cabrillo-san-diego",
    title: "Scenic Drive to Cabrillo & Point Loma, San Diego",
    metaDescription:
      "Drive out the Point Loma peninsula to Cabrillo National Monument — sweeping views of San Diego Bay, the skyline, the harbor, and the open ocean from the point.",
    intro:
      "The drive out Point Loma ends at Cabrillo, the high tip of the peninsula, with San Diego Bay and the skyline on one side and the open Pacific on the other.",
    region: "San Diego",
    mode: "driving",
    time: "20 min",
    distance: "6 miles",
    pleasantness: "High",
    whyPleasant: [
      "Climbs the spine of the Point Loma peninsula",
      "Bay, harbor and skyline views on the way out",
      "Ends at the high, open headland at Cabrillo",
      "Tide pools and a historic lighthouse at the point",
      "Whale-watching overlooks in winter",
    ],
    whatYoullSee: [
      "San Diego Bay and the downtown skyline",
      "The Old Point Loma Lighthouse",
      "Navy ships moving in and out of the harbor",
      "Tide pools on the ocean side of the point",
    ],
    bestTime: "Morning for clear bay views, or winter for gray-whale migration.",
    start: { name: "Point Loma (Catalina Blvd)", coords: [-117.2410, 32.7140] },
    end: { name: "Cabrillo National Monument", coords: [-117.2410, 32.6722] },
  },

  // ── Seattle ───────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-lake-washington-boulevard-seattle",
    title: "Scenic Drive on Lake Washington Boulevard, Seattle",
    metaDescription:
      "Drive Lake Washington Boulevard along Seattle's lakeshore — a slow, tree-lined Olmsted parkway past beaches, parks and constant views across the water to the Cascades.",
    intro:
      "Lake Washington Boulevard is Seattle's lakeshore parkway — an Olmsted-designed road that curves along the water past a string of beaches and parks, the Cascades on the far shore.",
    region: "Seattle",
    mode: "driving",
    time: "30 min",
    distance: "9 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Follows the Lake Washington shoreline almost the whole way",
      "An original Olmsted parkway — built to feel like a park",
      "Big-leaf maples and a continuous tree canopy",
      "Views across the lake to the Cascade Mountains",
      "Closed to cars on summer 'Bicycle Sunday' weekends",
    ],
    whatYoullSee: [
      "Seward Park's old-growth peninsula",
      "Mount Rainier across the water on clear days",
      "Leschi and Madrona lakeside beaches",
      "The Cascades on the eastern horizon",
    ],
    bestTime: "A clear summer evening, or a Bicycle Sunday when the road belongs to walkers and cyclists.",
    start: { name: "Seward Park, Seattle", coords: [-122.2585, 47.5510] },
    end: { name: "Madison Park, Seattle", coords: [-122.2770, 47.6360] },
  },
  {
    slug: "scenic-drive-chuckanut-drive",
    title: "Scenic Drive on Chuckanut Drive",
    metaDescription:
      "Drive Chuckanut Drive (Highway 11) north of Seattle — a cliff-hugging coast road above Samish Bay with island views, old-growth forest and the San Juans on the horizon.",
    intro:
      "Chuckanut Drive carves along the cliffs above Samish Bay, one of the Northwest's great coast roads — forest on one side, the islands of the Salish Sea on the other.",
    region: "Seattle",
    mode: "driving",
    time: "30 min",
    distance: "10 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Cliffside coast road above Samish Bay",
      "Old-growth cedar and fir pressing in from the hillside",
      "Views out to the San Juan Islands",
      "Oyster farms and pullouts along the water",
      "A genuine break from the I-5 corridor",
    ],
    whatYoullSee: [
      "Samish Bay and the Salish Sea",
      "The San Juan Islands on the horizon",
      "Larrabee State Park and its rocky coves",
      "Oyster bars tucked along the road",
    ],
    bestTime: "Sunset on a clear evening, looking west over the islands.",
    start: { name: "Chuckanut Drive south end (Bow)", coords: [-122.3970, 48.5790] },
    end: { name: "Larrabee State Park", coords: [-122.4870, 48.6520] },
  },
  {
    slug: "scenic-walk-alki-beach-seattle",
    title: "Pleasant Waterfront Walk at Alki Beach, Seattle",
    metaDescription:
      "Walk the Alki Beach promenade in West Seattle — a flat shoreline path along Puget Sound with the downtown skyline across the water and the Olympics beyond.",
    intro:
      "Alki Beach is Seattle's seaside promenade — a flat, easy path along Puget Sound with the downtown skyline glittering across the water and the Olympic Mountains beyond.",
    region: "Seattle",
    mode: "walking",
    time: "1 hr",
    distance: "2.5 miles",
    pleasantness: "High",
    whyPleasant: [
      "Flat, separated shoreline promenade",
      "Puget Sound on one side the whole way",
      "Downtown Seattle skyline across the water",
      "The Olympic Mountains on the western horizon",
      "Driftwood, sand and a miniature Statue of Liberty",
    ],
    whatYoullSee: [
      "The Seattle skyline across Elliott Bay",
      "Ferries crossing the Sound",
      "Alki Point Lighthouse at the west end",
      "The Olympics on a clear day",
    ],
    bestTime: "A clear evening at sunset, with the light behind the Olympics.",
    start: { name: "Alki Beach Park (east)", coords: [-122.3870, 47.5860] },
    end: { name: "Alki Point Lighthouse", coords: [-122.4205, 47.5760] },
  },

  // ── Portland, Oregon ──────────────────────────────────────────────────
  {
    slug: "scenic-drive-historic-columbia-river-highway",
    title: "Scenic Drive on the Historic Columbia River Highway",
    metaDescription:
      "Drive the Historic Columbia River Highway east of Portland — a 1910s scenic road past Vista House, Latourell, Bridal Veil, Wahkeena and Multnomah Falls.",
    intro:
      "The Historic Columbia River Highway was built in 1915 purely for the scenery — a narrow, mossy road that loops past one waterfall after another on its way into the Gorge.",
    region: "Portland",
    mode: "driving",
    time: "45 min",
    distance: "16 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "America's first scenic highway, built for the view",
      "Passes a string of waterfalls in just a few miles",
      "Mossy basalt cliffs and old stone guardrails",
      "Deep Douglas-fir forest and constant green",
      "The Vista House overlook high above the Columbia",
    ],
    whatYoullSee: [
      "Crown Point and the Vista House",
      "Latourell, Bridal Veil and Wahkeena Falls",
      "Multnomah Falls and its arched footbridge",
      "The Columbia River far below in the Gorge",
    ],
    bestTime: "Spring, when the falls are at full volume — early morning to beat the crowds at Multnomah.",
    start: { name: "Crown Point / Vista House", coords: [-122.2447, 45.5395] },
    end: { name: "Multnomah Falls", coords: [-122.1158, 45.5762] },
  },
  {
    slug: "scenic-drive-skyline-boulevard-portland",
    title: "Scenic Drive on Skyline Boulevard, Portland",
    metaDescription:
      "Drive Skyline Boulevard along the ridge above Portland — a quiet, wooded road on the edge of Forest Park with farms, big firs and glimpses of the Cascades.",
    intro:
      "Skyline Boulevard runs the wooded ridge on Portland's western edge, along the top of Forest Park — a quiet country road inside the city, with the Cascades showing through the trees.",
    region: "Portland",
    mode: "driving",
    time: "30 min",
    distance: "11 miles",
    pleasantness: "High",
    whyPleasant: [
      "Runs the forested ridge above the city",
      "Borders Forest Park, one of the largest urban forests in the US",
      "Tall Douglas-firs and small ridge-top farms",
      "Glimpses of Mount Hood and Mount St. Helens",
      "Quiet two-lane road, almost rural",
    ],
    whatYoullSee: [
      "Forest Park's tree canopy dropping away to the east",
      "Mount Hood and Mount St. Helens on clear days",
      "Small farms and orchards along the ridge",
      "Trailheads into the park's quiet interior",
    ],
    bestTime: "A clear day after rain, when the mountains stand out sharp to the east.",
    start: { name: "Skyline at Burnside", coords: [-122.7570, 45.5180] },
    end: { name: "Skyline at Germantown Road", coords: [-122.8000, 45.5780] },
  },
  {
    slug: "pleasant-walk-tom-mccall-waterfront-portland",
    title: "Pleasant Riverfront Walk on Portland's Waterfront",
    metaDescription:
      "Walk Tom McCall Waterfront Park along the Willamette River in downtown Portland — a flat, green riverside path past the bridges, fountains and cherry trees.",
    intro:
      "Tom McCall Waterfront Park gives downtown Portland a long green edge along the Willamette — a flat, easy walk past the city's bridges, with the water on one side and lawns on the other.",
    region: "Portland",
    mode: "walking",
    time: "45 min",
    distance: "2 miles",
    pleasantness: "High",
    whyPleasant: [
      "Follows the Willamette River through downtown",
      "Flat, wide, separated from traffic",
      "Passes under Portland's historic bridges",
      "A row of cherry trees that blooms in spring",
      "Fountains, lawns and constant river activity",
    ],
    whatYoullSee: [
      "The Hawthorne and Steel bridges",
      "The Japanese-American Historical Plaza cherry trees",
      "Salmon Street Springs fountain",
      "Rowers and paddlers on the Willamette",
    ],
    bestTime: "Late March to early April for the cherry blossoms, or any clear evening.",
    start: { name: "Salmon Street Springs", coords: [-122.6720, 45.5160] },
    end: { name: "Steel Bridge", coords: [-122.6680, 45.5285] },
  },

  // ── Boston ────────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-memorial-drive-charles-river",
    title: "Scenic Drive on Memorial Drive Along the Charles River",
    metaDescription:
      "Drive Memorial Drive along the Charles River between Cambridge and Boston — tree-lined parkway with the river, sailboats, and the Boston skyline across the water.",
    intro:
      "Memorial Drive runs the Cambridge bank of the Charles, a tree-lined parkway with the river and its sailboats on one side and the Boston skyline rising across the water.",
    region: "Boston",
    mode: "driving",
    time: "20 min",
    distance: "6 miles",
    pleasantness: "High",
    whyPleasant: [
      "Follows the Charles River the whole way",
      "Double rows of sycamores along the embankment",
      "The Boston skyline across the water",
      "Sailboats and rowers on the river",
      "Closed to cars on summer Sundays for the riverfront",
    ],
    whatYoullSee: [
      "The Boston skyline and the Hancock Tower across the river",
      "Sailboats from the community boathouse",
      "The MIT dome and Harvard's boathouses",
      "The Esplanade and Hatch Shell on the far bank",
    ],
    bestTime: "A summer Sunday when the road closes to cars, or any clear evening at golden hour.",
    start: { name: "Memorial Drive at BU Bridge", coords: [-71.1110, 42.3540] },
    end: { name: "Harvard Bridge / MIT", coords: [-71.0920, 42.3550] },
  },
  {
    slug: "pleasant-walk-emerald-necklace-boston",
    title: "Pleasant Walk on Boston's Emerald Necklace",
    metaDescription:
      "Walk the Emerald Necklace in Boston — Frederick Law Olmsted's chain of connected parks linking the Back Bay Fens, the Riverway and Jamaica Pond by water and green.",
    intro:
      "The Emerald Necklace is Olmsted's chain of connected parks threading through Boston — a continuous green walk along ponds, streams and shaded paths from the Fens to Jamaica Pond.",
    region: "Boston",
    mode: "walking",
    time: "1 hr 15 min",
    distance: "3.5 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "An unbroken chain of Olmsted-designed parks",
      "Follows the Muddy River and a string of ponds",
      "Mature trees and meadow the whole way",
      "Almost entirely separated from traffic",
      "Ends at the wide, calm Jamaica Pond",
    ],
    whatYoullSee: [
      "The Back Bay Fens and its community gardens",
      "The wooded Riverway along the Muddy River",
      "Olmsted Park's ponds",
      "Jamaica Pond, ringed by a 1.5-mile loop",
    ],
    bestTime: "Mid-October for the fall color along the Riverway, or any spring morning.",
    start: { name: "Back Bay Fens", coords: [-71.0970, 42.3430] },
    end: { name: "Jamaica Pond", coords: [-71.1200, 42.3170] },
  },
  {
    slug: "scenic-drive-cape-ann-route-127",
    title: "Scenic Drive Around Cape Ann on Route 127",
    metaDescription:
      "Drive Route 127 around Cape Ann north of Boston — the rocky New England coast through Gloucester and Rockport, with harbors, granite shores and open Atlantic views.",
    intro:
      "Route 127 loops around Cape Ann, the classic rocky-coast drive north of Boston — granite shores, working harbors, and the open Atlantic through Gloucester and Rockport.",
    region: "Boston",
    mode: "driving",
    time: "40 min",
    distance: "14 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Wraps the rocky Cape Ann coastline",
      "Working fishing harbors and granite shores",
      "Open Atlantic views around the headlands",
      "Classic New England villages and stone walls",
      "Far quieter than the Cape Cod beaches to the south",
    ],
    whatYoullSee: [
      "Gloucester Harbor and the Fishermen's Memorial",
      "Rockport's Bearskin Neck and the red fishing shack 'Motif No. 1'",
      "Halibut Point's granite quarry and tide pools",
      "Open ocean around the cape's headlands",
    ],
    bestTime: "Early autumn, when the light is clear and the summer crowds have thinned.",
    start: { name: "Gloucester Harbor", coords: [-70.6620, 42.6100] },
    end: { name: "Rockport (Bearskin Neck)", coords: [-70.6160, 42.6560] },
  },

  // ── Chicago ───────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-lake-shore-drive-chicago",
    title: "Scenic Drive on Lake Shore Drive, Chicago",
    metaDescription:
      "Drive Lake Shore Drive along Lake Michigan in Chicago — the skyline on one side, the open lake on the other, past the beaches, harbors and parks of the lakefront.",
    intro:
      "Lake Shore Drive is Chicago's great lakefront road — the skyline rising on one side, Lake Michigan stretching out flat and blue on the other, for miles.",
    region: "Chicago",
    mode: "driving",
    time: "25 min",
    distance: "9 miles",
    pleasantness: "High",
    whyPleasant: [
      "Runs the entire lakefront between the city and the water",
      "The Chicago skyline on full display",
      "Lake Michigan open to the horizon",
      "Beaches, harbors and parks the whole way",
      "No commercial frontage — park on one side, lake on the other",
    ],
    whatYoullSee: [
      "The downtown skyline from the south curve at Oakwood",
      "North Avenue and Oak Street beaches",
      "Belmont and Montrose harbors full of sailboats",
      "Lincoln Park and its lagoons",
    ],
    bestTime: "Early morning heading north, with sunrise over the lake and the skyline lit.",
    start: { name: "Museum Campus, Chicago", coords: [-87.6160, 41.8650] },
    end: { name: "Foster Avenue Beach", coords: [-87.6470, 41.9760] },
  },
  {
    slug: "pleasant-walk-chicago-lakefront-trail",
    title: "Pleasant Walk on the Chicago Lakefront Trail",
    metaDescription:
      "Walk the Chicago Lakefront Trail along Lake Michigan — a flat, separated path past beaches, harbors and the skyline, from the Museum Campus up through Lincoln Park.",
    intro:
      "The Lakefront Trail gives Chicago an 18-mile green edge along Lake Michigan — a flat, car-free path with the water on one side and the parks and skyline on the other.",
    region: "Chicago",
    mode: "walking",
    time: "1 hr",
    distance: "3 miles",
    pleasantness: "High",
    whyPleasant: [
      "Completely separated from traffic",
      "Lake Michigan beside you the whole way",
      "Beaches, harbors and wide park lawns",
      "Skyline views from the lakefront curves",
      "Flat and easy, walk or bike",
    ],
    whatYoullSee: [
      "North Avenue Beach and its boathouse",
      "The skyline framed across the water",
      "Diversey and Belmont harbors",
      "Lincoln Park's lagoons and conservatory",
    ],
    bestTime: "A clear morning when the lake is calm and the skyline catches the early light.",
    start: { name: "North Avenue Beach", coords: [-87.6270, 41.9120] },
    end: { name: "Fullerton / Lincoln Park", coords: [-87.6320, 41.9260] },
  },
  {
    slug: "scenic-drive-sheridan-road-north-shore",
    title: "Scenic Drive on Sheridan Road Through Chicago's North Shore",
    metaDescription:
      "Drive Sheridan Road north of Chicago through the leafy North Shore suburbs — old estates, ravines, lakefront mansions and the Northwestern campus along Lake Michigan.",
    intro:
      "Sheridan Road winds north out of the city through the old North Shore — a canopy road past lakefront estates, wooded ravines and college quads, with Lake Michigan never far.",
    region: "Chicago",
    mode: "driving",
    time: "35 min",
    distance: "12 miles",
    pleasantness: "High",
    whyPleasant: [
      "Tunnels through the canopy of the leafy North Shore",
      "Curves past historic lakefront estates",
      "Wooded ravines dropping to the lake",
      "The Northwestern lakefill and quads",
      "A slow, residential alternative to the expressway",
    ],
    whatYoullSee: [
      "Northwestern University's lakefront campus in Evanston",
      "The Baha'i House of Worship in Wilmette",
      "Lake-bluff mansions and ravine bridges",
      "Glimpses of Lake Michigan between the estates",
    ],
    bestTime: "Mid-to-late October for the North Shore's fall color under the old trees.",
    start: { name: "Evanston (Northwestern)", coords: [-87.6760, 42.0560] },
    end: { name: "Winnetka", coords: [-87.7360, 42.1080] },
  },

  // ── Washington, DC ────────────────────────────────────────────────────
  {
    slug: "scenic-drive-rock-creek-parkway-dc",
    title: "Scenic Drive on Rock Creek Parkway, Washington DC",
    metaDescription:
      "Drive Rock Creek and Potomac Parkway through the wooded heart of Washington DC — a tree-shaded creek road that feels like a forest, minutes from the monuments.",
    intro:
      "Rock Creek Parkway threads the wooded valley that cuts through the middle of Washington — a shaded creek road that feels like deep forest, a few minutes from the monuments.",
    region: "Washington DC",
    mode: "driving",
    time: "20 min",
    distance: "5 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Follows Rock Creek through a wooded valley inside the city",
      "Dense tree canopy and the sound of the creek",
      "Stone bridges and 1930s parkway design",
      "Car-free on weekends for walkers and cyclists",
      "Connects the forest of Rock Creek Park to the Potomac",
    ],
    whatYoullSee: [
      "Rock Creek tumbling beside the road",
      "Old stone arch bridges",
      "The wooded slopes of Rock Creek Park",
      "The Kennedy Center and Potomac at the southern end",
    ],
    bestTime: "A weekend morning when the upper parkway closes to cars, or peak fall color in late October.",
    start: { name: "Rock Creek Park (Broad Branch)", coords: [-77.0500, 38.9560] },
    end: { name: "Kennedy Center / Potomac", coords: [-77.0560, 38.8960] },
  },
  {
    slug: "scenic-drive-george-washington-parkway",
    title: "Scenic Drive on the George Washington Memorial Parkway",
    metaDescription:
      "Drive the George Washington Memorial Parkway along the Potomac River near Washington DC — river views, the DC skyline, Mount Vernon, and continuous forested banks.",
    intro:
      "The George Washington Parkway runs the Virginia bank of the Potomac, a designed scenic road with the river on one side, forest on the other, and the monuments across the water.",
    region: "Washington DC",
    mode: "driving",
    time: "30 min",
    distance: "12 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Follows the Potomac River the whole way",
      "Forested banks and a true scenic-parkway design",
      "Views across the river to the DC monuments",
      "Overlooks above the river gorge",
      "Connects to Mount Vernon at its southern end",
    ],
    whatYoullSee: [
      "The Washington Monument and Lincoln Memorial across the river",
      "Theodore Roosevelt Island's wooded shore",
      "Planes dropping into Reagan National over the water",
      "Old Town Alexandria's waterfront",
    ],
    bestTime: "Early spring, with the cherry and dogwood blooming along the river, or a clear fall afternoon.",
    start: { name: "Key Bridge / Rosslyn", coords: [-77.0720, 38.9020] },
    end: { name: "Old Town Alexandria", coords: [-77.0420, 38.8050] },
  },
  {
    slug: "pleasant-walk-tidal-basin-dc",
    title: "Pleasant Walk Around the Tidal Basin, Washington DC",
    metaDescription:
      "Walk the loop around the Tidal Basin in Washington DC — cherry trees, the Jefferson and MLK memorials, and water views of the monuments reflected across the basin.",
    intro:
      "The loop around the Tidal Basin is the gentlest walk in Washington — a flat path under the famous cherry trees, ringing the water past four memorials.",
    region: "Washington DC",
    mode: "walking",
    time: "45 min",
    distance: "2.1 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Flat, paved loop right at the water's edge",
      "Ringed by the famous Japanese cherry trees",
      "Passes four monuments in two miles",
      "The Jefferson Memorial reflected across the basin",
      "Constant water views and open sky",
    ],
    whatYoullSee: [
      "The Jefferson Memorial across the water",
      "The Martin Luther King Jr. Memorial",
      "The FDR Memorial's water-and-stone rooms",
      "Cherry blossoms in late March / early April",
    ],
    bestTime: "Peak cherry-blossom bloom in late March, or sunrise any time for empty paths and soft light.",
    start: { name: "Jefferson Memorial", coords: [-77.0365, 38.8814] },
    end: { name: "Martin Luther King Jr. Memorial", coords: [-77.0444, 38.8861] },
  },

  // ── Denver ────────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-lariat-loop-lookout-mountain",
    title: "Scenic Drive on the Lariat Loop up Lookout Mountain",
    metaDescription:
      "Drive the Lariat Loop switchbacks up Lookout Mountain near Denver — a historic mountain road climbing out of Golden to sweeping views of the plains and the Front Range.",
    intro:
      "The Lariat Loop switchbacks straight up Lookout Mountain out of Golden — a historic 1910s mountain road that climbs fast to a long view back over Denver and the plains.",
    region: "Denver",
    mode: "driving",
    time: "25 min",
    distance: "8 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Tight switchbacks climbing the face of Lookout Mountain",
      "Sweeping views back over Golden and the Denver plains",
      "Pine forest and red-rock outcrops on the climb",
      "A historic scenic byway built in 1914",
      "Buffalo Bill's grave and a nature center at the top",
    ],
    whatYoullSee: [
      "Golden and the plains dropping away below",
      "The Front Range peaks to the west",
      "Buffalo Bill Museum and Grave at the summit",
      "Hang gliders launching from the overlooks",
    ],
    bestTime: "A clear morning for the long view east, or sunset for the light on the Front Range.",
    start: { name: "Golden, Colorado", coords: [-105.2210, 39.7555] },
    end: { name: "Lookout Mountain summit", coords: [-105.2390, 39.7320] },
  },
  {
    slug: "scenic-drive-red-rocks-bear-creek-canyon",
    title: "Scenic Drive Through Red Rocks & Bear Creek Canyon",
    metaDescription:
      "Drive from Red Rocks Park up Bear Creek Canyon near Denver — towering red sandstone formations, a creek-side canyon road, and the foothills town of Evergreen.",
    intro:
      "This drive starts among the giant red sandstone fins of Red Rocks, then follows Bear Creek up its canyon into the foothills — rock, water and pine in quick succession.",
    region: "Denver",
    mode: "driving",
    time: "30 min",
    distance: "13 miles",
    pleasantness: "High",
    whyPleasant: [
      "Begins among the towering red rocks of Red Rocks Park",
      "Follows Bear Creek up a tight foothills canyon",
      "Creek on one side, canyon walls on the other",
      "Pine forest and quick elevation gain",
      "Ends at the lake in mountain-town Evergreen",
    ],
    whatYoullSee: [
      "The red sandstone formations and amphitheatre at Red Rocks",
      "Bear Creek running beside the road",
      "Canyon walls and ponderosa pines",
      "Evergreen Lake at the top of the canyon",
    ],
    bestTime: "Morning light on the red rocks, then up the cool canyon as the day warms.",
    start: { name: "Red Rocks Park, Morrison", coords: [-105.2057, 39.6655] },
    end: { name: "Evergreen Lake", coords: [-105.3270, 39.6320] },
  },
  {
    slug: "pleasant-walk-cherry-creek-trail-denver",
    title: "Pleasant Walk on the Cherry Creek Trail, Denver",
    metaDescription:
      "Walk the Cherry Creek Trail through downtown Denver — a flat, below-street creek path lined with cottonwoods, away from the traffic, with the mountains on the skyline.",
    intro:
      "The Cherry Creek Trail runs below street level through the heart of Denver, following the creek under the bridges — a quiet green corridor with the Front Range on the horizon.",
    region: "Denver",
    mode: "walking",
    time: "45 min",
    distance: "2.5 miles",
    pleasantness: "High",
    whyPleasant: [
      "Follows Cherry Creek below the street grid",
      "Separated from traffic, under the bridges",
      "Cottonwoods and willows along the water",
      "Front Range views where the trail opens up",
      "Links downtown to the Cherry Creek neighborhood",
    ],
    whatYoullSee: [
      "The creek and its little urban rapids",
      "Confluence Park where Cherry Creek meets the South Platte",
      "Downtown Denver's towers above the banks",
      "The Front Range on the western skyline",
    ],
    bestTime: "A clear morning when the mountains are sharp to the west.",
    start: { name: "Confluence Park, Denver", coords: [-105.0030, 39.7545] },
    end: { name: "Cherry Creek North", coords: [-104.9620, 39.7180] },
  },

  // ── Austin ────────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-capital-of-texas-highway-360",
    title: "Scenic Drive on the Capital of Texas Highway (Loop 360)",
    metaDescription:
      "Drive Loop 360, the Capital of Texas Highway, through the hills west of Austin — limestone cliffs, Hill Country views, and the landmark Pennybacker Bridge over Lake Austin.",
    intro:
      "Loop 360 rolls through the Hill Country edge of Austin — limestone road cuts, oak-covered hills, and the soaring Pennybacker Bridge arcing over the blue of Lake Austin.",
    region: "Austin",
    mode: "driving",
    time: "25 min",
    distance: "10 miles",
    pleasantness: "High",
    whyPleasant: [
      "Cuts through the limestone hills west of the city",
      "Oak and cedar Hill Country, not strip development",
      "Crosses the landmark Pennybacker (360) Bridge",
      "Views over Lake Austin and the Colorado River",
      "Rolling grades and long green sightlines",
    ],
    whatYoullSee: [
      "The Pennybacker Bridge arcing over Lake Austin",
      "Limestone cliffs and Hill Country oaks",
      "The overlook above the lake on the north bank",
      "Bull Creek's greenbelt crossings",
    ],
    bestTime: "Late afternoon, when the light warms the limestone and the lake turns gold.",
    start: { name: "Loop 360 at MoPac", coords: [-97.8030, 30.2980] },
    end: { name: "Pennybacker Bridge overlook", coords: [-97.7990, 30.3580] },
  },
  {
    slug: "pleasant-walk-lady-bird-lake-austin",
    title: "Pleasant Walk on the Lady Bird Lake Trail, Austin",
    metaDescription:
      "Walk the Ann and Roy Butler Hike-and-Bike Trail around Lady Bird Lake in Austin — a flat, shaded loop along the water with downtown skyline and cypress-lined banks.",
    intro:
      "The trail around Lady Bird Lake is Austin's green heart — a flat, shaded path along the water, cypress trees leaning over the banks and the downtown skyline across the lake.",
    region: "Austin",
    mode: "walking",
    time: "1 hr",
    distance: "3 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Loops the calm water of Lady Bird Lake",
      "Shaded by bald cypress and pecan trees",
      "Flat, soft-surface, separated from traffic",
      "Downtown skyline reflected across the lake",
      "The bat colony emerging from Congress Bridge at dusk",
    ],
    whatYoullSee: [
      "The downtown Austin skyline across the water",
      "Cypress trees along the banks",
      "Paddleboarders and kayakers on the lake",
      "The Congress Avenue Bridge bats at sunset",
    ],
    bestTime: "Early morning for cool shade, or sunset near the Congress Bridge for the bats.",
    start: { name: "Auditorium Shores, Austin", coords: [-97.7510, 30.2620] },
    end: { name: "Lou Neff Point", coords: [-97.7700, 30.2640] },
  },
  {
    slug: "scenic-drive-mount-bonnell-austin",
    title: "Scenic Drive to Mount Bonnell, Austin",
    metaDescription:
      "Drive up to Mount Bonnell, the highest point in Austin — a winding road through the hills above Lake Austin to a clifftop overlook of the river, the hills and the city.",
    intro:
      "The short climb to Mount Bonnell winds up through the wooded hills above Lake Austin to the city's highest point — a clifftop with the river curving far below.",
    region: "Austin",
    mode: "driving",
    time: "15 min",
    distance: "4 miles",
    pleasantness: "High",
    whyPleasant: [
      "Winds up through the wooded hills west of the city",
      "Ends at Austin's highest overlook",
      "Cliffs above the blue of Lake Austin",
      "Oaks, cedar and limestone the whole climb",
      "A short, classic Austin sunset spot",
    ],
    whatYoullSee: [
      "Lake Austin curving through the hills below",
      "Hill Country rolling away to the west",
      "The downtown skyline to the southeast",
      "Cliffside homes tucked into the canyon",
    ],
    bestTime: "Sunset — climb the steps at the top for the full view as the light goes.",
    start: { name: "Mount Bonnell Road", coords: [-97.7780, 30.3140] },
    end: { name: "Mount Bonnell summit", coords: [-97.7730, 30.3210] },
  },

  // ── Miami ─────────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-rickenbacker-causeway-key-biscayne",
    title: "Scenic Drive on the Rickenbacker Causeway to Key Biscayne",
    metaDescription:
      "Drive the Rickenbacker Causeway from Miami out to Key Biscayne — over the bay on a high bridge with open water all around and the downtown Miami skyline behind you.",
    intro:
      "The Rickenbacker Causeway lifts you off the mainland and out over Biscayne Bay to Key Biscayne — open turquoise water on both sides and the Miami skyline filling the mirror.",
    region: "Miami",
    mode: "driving",
    time: "20 min",
    distance: "7 miles",
    pleasantness: "High",
    whyPleasant: [
      "Crosses Biscayne Bay on a high open bridge",
      "Turquoise water on both sides the whole way",
      "The Miami skyline rising behind you",
      "Palm-lined beaches along the causeway",
      "Ends in the parks and lighthouse of Key Biscayne",
    ],
    whatYoullSee: [
      "The downtown Miami skyline across the bay",
      "Hobie Island and Virginia Key beaches",
      "Kiteboarders and sailboats on the bay",
      "The Cape Florida Lighthouse at the island's tip",
    ],
    bestTime: "Early morning for calm water and light traffic, or sunset with the skyline lighting up.",
    start: { name: "Rickenbacker Causeway (mainland)", coords: [-80.2090, 25.7470] },
    end: { name: "Bill Baggs / Cape Florida, Key Biscayne", coords: [-80.1570, 25.6680] },
  },
  {
    slug: "scenic-drive-ocean-drive-a1a-miami-beach",
    title: "Scenic Drive on Ocean Drive & A1A, Miami Beach",
    metaDescription:
      "Drive Ocean Drive and Collins Avenue (A1A) along Miami Beach — pastel Art Deco hotels, palm-lined sand, and the open Atlantic beside you the whole way.",
    intro:
      "Ocean Drive and the A1A run the length of Miami Beach — pastel Art Deco facades on one side, palm-lined sand and the open Atlantic on the other.",
    region: "Miami",
    mode: "driving",
    time: "25 min",
    distance: "8 miles",
    pleasantness: "High",
    whyPleasant: [
      "Runs the Atlantic beachfront of Miami Beach",
      "The pastel Art Deco Historic District",
      "Palms, sand and ocean beside you",
      "Lummus Park's green strip between road and beach",
      "Easy, flat, made for cruising slowly",
    ],
    whatYoullSee: [
      "The Art Deco hotels of Ocean Drive",
      "Lummus Park and the lifeguard stands",
      "The open Atlantic and South Beach sand",
      "The marina and causeways toward the north end",
    ],
    bestTime: "Early evening when the Deco neon comes on, or early morning for empty sand and soft light.",
    start: { name: "South Pointe, Miami Beach", coords: [-80.1300, 25.7680] },
    end: { name: "Mid-Beach (Collins Ave)", coords: [-80.1240, 25.8120] },
  },
  {
    slug: "scenic-drive-old-cutler-road-miami",
    title: "Scenic Drive on Old Cutler Road, Miami",
    metaDescription:
      "Drive Old Cutler Road south of Miami — a historic road tunneled under a canopy of giant banyan trees, past Matheson Hammock, the gardens and old Coral Gables.",
    intro:
      "Old Cutler Road is Miami's canopy drive — a historic road tunneled beneath enormous banyan and live-oak trees, their branches meeting overhead the whole way south.",
    region: "Miami",
    mode: "driving",
    time: "20 min",
    distance: "7 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Tunnels under a continuous canopy of giant banyans",
      "Historic road through old Coral Gables and Pinecrest",
      "Dappled green light the whole way",
      "Passes Matheson Hammock and Fairchild gardens",
      "A cool, shaded contrast to the open causeways",
    ],
    whatYoullSee: [
      "The arching banyan and live-oak canopy",
      "Matheson Hammock Park and its atoll pool",
      "Fairchild Tropical Botanic Garden",
      "Old coral-rock walls and Gables landmarks",
    ],
    bestTime: "Late morning, when sun filters down through the canopy in patches.",
    start: { name: "Cocoplum Circle, Coral Gables", coords: [-80.2680, 25.7160] },
    end: { name: "Matheson Hammock Park", coords: [-80.2730, 25.6810] },
  },

  // ── London ────────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-richmond-park-london",
    title: "Scenic Drive Through Richmond Park, London",
    metaDescription:
      "Drive through Richmond Park in London — the city's largest royal park, with ancient oaks, roaming red deer, ponds, and a protected view all the way to St Paul's.",
    intro:
      "Richmond Park is London's wild edge — the largest of the royal parks, where red deer roam beneath 700-year-old oaks and a single sightline runs all the way to St Paul's.",
    region: "London",
    mode: "driving",
    time: "20 min",
    distance: "3 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "London's largest royal park, mostly open grass and ancient woodland",
      "Herds of red and fallow deer roaming free",
      "700-year-old oaks and the Isabella Plantation",
      "The protected 'King Henry's Mound' view to St Paul's",
      "A 20mph park road, calm and slow by design",
    ],
    whatYoullSee: [
      "Red deer grazing beside the road",
      "Pen Ponds in the middle of the park",
      "Ancient oaks scattered across the grassland",
      "The distant dome of St Paul's from King Henry's Mound",
    ],
    bestTime: "Early on an autumn morning during the deer rut, or any clear day at golden hour.",
    start: { name: "Richmond Gate", coords: [-0.2935, 51.4520] },
    end: { name: "Roehampton Gate", coords: [-0.2660, 51.4470] },
  },
  {
    slug: "scenic-walk-thames-path-richmond-kew",
    title: "Pleasant Riverside Walk on the Thames Path: Richmond to Kew",
    metaDescription:
      "Walk the Thames Path from Richmond to Kew in London — a flat, leafy towpath along the river past meadows, boathouses, and the gardens at Kew.",
    intro:
      "The Thames Path between Richmond and Kew is the gentlest walk in London — a flat, tree-lined towpath along the river, with meadows on one bank and the city forgotten behind you.",
    region: "London",
    mode: "walking",
    time: "1 hr",
    distance: "3 miles",
    pleasantness: "High",
    whyPleasant: [
      "Flat riverside towpath the whole way",
      "Plane trees and willows along the bank",
      "Rowers, narrowboats and swans on the Thames",
      "Petersham Meadows and grazing cows beside the path",
      "Ends near the gates of Kew Gardens",
    ],
    whatYoullSee: [
      "Richmond Bridge and the riverside cafés",
      "Petersham Meadows and Marble Hill across the water",
      "Syon House and its park on the far bank",
      "Kew Gardens and Kew Bridge at the end",
    ],
    bestTime: "A clear morning at low tide, or late spring when the meadows are green.",
    start: { name: "Richmond Bridge", coords: [-0.3080, 51.4560] },
    end: { name: "Kew Bridge", coords: [-0.2855, 51.4880] },
  },
  {
    slug: "scenic-walk-royal-parks-london",
    title: "Pleasant Walk Through London's Royal Parks",
    metaDescription:
      "Walk across central London entirely through parks — from Kensington Gardens through Hyde Park and Green Park to St James's Park, almost without touching a road.",
    intro:
      "You can cross the middle of London almost entirely on grass — a chain of royal parks links Kensington to Westminster, lakes and avenues the whole way.",
    region: "London",
    mode: "walking",
    time: "1 hr",
    distance: "2.5 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Crosses central London almost entirely through parkland",
      "Links four royal parks in a row",
      "Lakes, avenues of plane trees and open lawns",
      "Pelicans, ducks and rose gardens along the way",
      "Ends with the view to Buckingham Palace",
    ],
    whatYoullSee: [
      "The Serpentine lake in Hyde Park",
      "The Italian Gardens in Kensington Gardens",
      "Green Park's avenues of trees",
      "The lake and pelicans of St James's Park, Buckingham Palace beyond",
    ],
    bestTime: "Early morning for empty paths, or late spring when the gardens are in bloom.",
    start: { name: "Kensington Gardens", coords: [-0.1800, 51.5060] },
    end: { name: "St James's Park", coords: [-0.1340, 51.5020] },
  },

  // ── Paris ─────────────────────────────────────────────────────────────
  {
    slug: "scenic-walk-seine-quays-paris",
    title: "Pleasant Riverside Walk Along the Seine, Paris",
    metaDescription:
      "Walk the quays of the Seine through the heart of Paris — past Notre-Dame, the Louvre, the bridges and bookstalls, with the river at your side to the Eiffel Tower.",
    intro:
      "The walk along the Seine is Paris distilled — riverside quays past Notre-Dame, the Louvre and a dozen bridges, the city's whole skyline laid out along the water.",
    region: "Paris",
    mode: "walking",
    time: "1 hr 15 min",
    distance: "3 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Follows the Seine through the historic heart of Paris",
      "Lower quays are car-free riverside promenades",
      "Passes the city's great monuments one after another",
      "Plane trees, bouquinistes and bridges the whole way",
      "Ends beneath the Eiffel Tower",
    ],
    whatYoullSee: [
      "Notre-Dame and the Île de la Cité",
      "The Louvre and the Pont des Arts",
      "The green bookstalls along the parapets",
      "The Eiffel Tower from the Pont de Bir-Hakeim",
    ],
    bestTime: "Late afternoon into the blue hour, when the monuments begin to light up.",
    start: { name: "Île de la Cité / Notre-Dame", coords: [2.3490, 48.8530] },
    end: { name: "Pont de Bir-Hakeim / Eiffel Tower", coords: [2.2900, 48.8550] },
  },
  {
    slug: "scenic-walk-canal-saint-martin-paris",
    title: "Pleasant Walk Along the Canal Saint-Martin, Paris",
    metaDescription:
      "Walk the Canal Saint-Martin in Paris — a tree-lined canal with iron footbridges and locks, the quiet, local side of the city away from the monuments.",
    intro:
      "The Canal Saint-Martin is the everyday, local Paris — a tree-shaded canal crossed by arching iron footbridges, with locks lifting the boats and cafés along the banks.",
    region: "Paris",
    mode: "walking",
    time: "40 min",
    distance: "1.5 miles",
    pleasantness: "High",
    whyPleasant: [
      "Follows the tree-lined Canal Saint-Martin",
      "Arched iron footbridges and working locks",
      "Quiet, residential, distinctly local",
      "Plane trees shading the towpaths",
      "Cafés and small shops along the banks",
    ],
    whatYoullSee: [
      "The cast-iron footbridges over the canal",
      "Boats rising and falling in the locks",
      "Tree-shaded quays and waterside cafés",
      "The wide Bassin de la Villette at the north end",
    ],
    bestTime: "A weekend afternoon when locals gather on the banks, or a quiet weekday morning.",
    start: { name: "Place de la République", coords: [2.3640, 48.8675] },
    end: { name: "Bassin de la Villette", coords: [2.3720, 48.8830] },
  },

  // ── Sydney ────────────────────────────────────────────────────────────
  {
    slug: "scenic-walk-bondi-to-coogee-sydney",
    title: "Scenic Coastal Walk from Bondi to Coogee, Sydney",
    metaDescription:
      "Walk the Bondi to Coogee coastal path in Sydney — a clifftop trail past beaches, rock pools and headlands, with the open Pacific beside you the whole way.",
    intro:
      "The Bondi to Coogee walk is Sydney's great coastal path — a clifftop trail linking a string of beaches and rock pools, the Pacific crashing below the whole way.",
    region: "Sydney",
    mode: "walking",
    time: "1 hr 30 min",
    distance: "3.7 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Clifftop coastal path the entire length",
      "Links Bondi, Tamarama, Bronte, Clovelly and Coogee beaches",
      "Sandstone headlands and ocean rock pools",
      "Open Pacific views and sea air throughout",
      "Whale-watching from the cliffs in season",
    ],
    whatYoullSee: [
      "Bondi Beach and the Icebergs ocean pool",
      "The cliffs and sandstone headlands",
      "Bronte and Clovelly's sheltered swimming spots",
      "Waverley Cemetery on its dramatic clifftop",
    ],
    bestTime: "Early morning before it gets busy, or May–November for migrating whales.",
    start: { name: "Bondi Beach", coords: [151.2740, -33.8910] },
    end: { name: "Coogee Beach", coords: [151.2590, -33.9200] },
  },
  {
    slug: "scenic-drive-grand-pacific-drive-sea-cliff-bridge",
    title: "Scenic Drive on the Grand Pacific Drive & Sea Cliff Bridge",
    metaDescription:
      "Drive the Grand Pacific Drive south of Sydney — from the Bald Hill lookout down the escarpment and out over the ocean on the famous Sea Cliff Bridge.",
    intro:
      "South of Sydney the road runs out of cliff to cling to — so it leaps onto the Sea Cliff Bridge, curving out over the open ocean below the escarpment.",
    region: "Sydney",
    mode: "driving",
    time: "20 min",
    distance: "6 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Drops down the forested Illawarra escarpment to the sea",
      "Crosses the Sea Cliff Bridge out over the ocean",
      "Big coastal views from the Bald Hill lookout",
      "Rainforest on one side, Pacific on the other",
      "A genuine destination drive, not a commute road",
    ],
    whatYoullSee: [
      "The Bald Hill lookout over Stanwell Park",
      "Hang gliders launching from the headland",
      "The Sea Cliff Bridge curving over the water",
      "The escarpment forest dropping to the coast",
    ],
    bestTime: "Morning light on the bridge, or a clear day when the ocean is deep blue.",
    start: { name: "Bald Hill Lookout, Stanwell Tops", coords: [150.9930, -34.2300] },
    end: { name: "Coalcliff (Sea Cliff Bridge)", coords: [150.9760, -34.2440] },
  },
  {
    slug: "scenic-walk-spit-to-manly-sydney",
    title: "Scenic Harbour Walk from the Spit to Manly, Sydney",
    metaDescription:
      "Walk the Spit to Manly track in Sydney — a harbour-side trail through bushland and secluded beaches, with views across the water and the heads beyond.",
    intro:
      "The Spit to Manly is Sydney Harbour at its wildest — a bush track that ducks between hidden beaches and headlands, the water flashing blue through the trees.",
    region: "Sydney",
    mode: "walking",
    time: "3 hr",
    distance: "6 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Follows the bushland edge of Sydney Harbour",
      "Strings together secluded harbour beaches",
      "Native bush, sandstone and angophora trees",
      "Constant water views across to the North Shore",
      "Ends at the ferry and beaches of Manly",
    ],
    whatYoullSee: [
      "Hidden beaches like Clontarf and Reef Beach",
      "Grotto Point Lighthouse and Aboriginal rock engravings",
      "The harbour opening toward the heads",
      "Manly's beachfront and the ferry home",
    ],
    bestTime: "A clear, cool morning — the full track takes a few hours, so start early.",
    start: { name: "Spit Bridge, Mosman", coords: [151.2470, -33.8030] },
    end: { name: "Manly Wharf", coords: [151.2850, -33.7990] },
  },

  // ── Vancouver ─────────────────────────────────────────────────────────
  {
    slug: "scenic-walk-stanley-park-seawall-vancouver",
    title: "Scenic Walk on the Stanley Park Seawall, Vancouver",
    metaDescription:
      "Walk the Stanley Park Seawall in Vancouver — a flat, car-free path around a forested peninsula, with the sea on one side, old-growth trees and the mountains beyond.",
    intro:
      "The Stanley Park Seawall wraps a whole forested peninsula on the edge of downtown Vancouver — a flat, car-free path with the sea on one side and old-growth cedar on the other.",
    region: "Vancouver",
    mode: "walking",
    time: "1 hr 30 min",
    distance: "5.5 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Car-free seawall the whole way around the park",
      "Sea on one side, old-growth rainforest on the other",
      "Views of the North Shore mountains across the water",
      "Totem poles, beaches and the Lions Gate Bridge",
      "Flat and easy — walk, run or bike",
    ],
    whatYoullSee: [
      "The downtown skyline and Coal Harbour",
      "The North Shore mountains across Burrard Inlet",
      "The Brockton Point totem poles and lighthouse",
      "Siwash Rock and the Lions Gate Bridge",
    ],
    bestTime: "A clear morning when the mountains are sharp, or sunset from the English Bay side.",
    start: { name: "Coal Harbour (Seawall start)", coords: [-123.1290, 49.2920] },
    end: { name: "English Bay / Second Beach", coords: [-123.1430, 49.2900] },
  },
  {
    slug: "scenic-drive-sea-to-sky-highway-squamish",
    title: "Scenic Drive on the Sea-to-Sky Highway to Squamish",
    metaDescription:
      "Drive the Sea-to-Sky Highway north of Vancouver — Highway 99 along Howe Sound with fjord views, waterfalls, and the granite face of the Stawamus Chief at Squamish.",
    intro:
      "The Sea-to-Sky climbs north out of Vancouver along the fjord of Howe Sound — ocean on one side, granite cliffs and waterfalls on the other, all the way to Squamish.",
    region: "Vancouver",
    mode: "driving",
    time: "45 min",
    distance: "28 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Runs along the fjord of Howe Sound",
      "Sea below, coastal mountains and granite walls above",
      "Passes Shannon Falls, one of BC's tallest waterfalls",
      "Constant ocean-and-mountain views",
      "Ends beneath the Stawamus Chief at Squamish",
    ],
    whatYoullSee: [
      "Howe Sound and its islands",
      "Shannon Falls dropping beside the road",
      "The granite face of the Stawamus Chief",
      "The Sea to Sky Gondola climbing the mountainside",
    ],
    bestTime: "A clear day for the fjord and mountain views; spring for the waterfalls at full flow.",
    start: { name: "Horseshoe Bay, West Vancouver", coords: [-123.2740, 49.3760] },
    end: { name: "Squamish", coords: [-123.1560, 49.7010] },
  },

  // ── Melbourne ─────────────────────────────────────────────────────────
  {
    slug: "scenic-drive-great-ocean-road",
    title: "Scenic Drive on the Great Ocean Road",
    metaDescription:
      "Drive the Great Ocean Road from Torquay to Apollo Bay — one of the world's great coastal drives, hugging the cliffs of Australia's southern coast past surf beaches and rainforest.",
    intro:
      "The Great Ocean Road is one of the world's great coast drives — carved along the cliffs of Australia's southern edge, swinging from surf beaches into rainforest and back to the sea.",
    region: "Melbourne",
    mode: "driving",
    time: "1 hr 45 min",
    distance: "57 miles",
    pleasantness: "Very High",
    whyPleasant: [
      "Hugs the cliffs of Australia's wild southern coast",
      "Built by returned WWI soldiers as a coastal memorial road",
      "Swings between surf beaches, headlands and rainforest",
      "Koalas in the gums and the Otway forest inland",
      "One of the most celebrated scenic drives anywhere",
    ],
    whatYoullSee: [
      "The surf coast at Bells Beach and Torquay",
      "The Memorial Arch at Eastern View",
      "Cliffs and lighthouses around Lorne and Aireys Inlet",
      "Rainforest and koalas in the Otways near Apollo Bay",
    ],
    bestTime: "A clear day with time to stop often — the drive is the destination, so don't rush it.",
    start: { name: "Torquay", coords: [144.3260, -38.3380] },
    end: { name: "Apollo Bay", coords: [143.6720, -38.7560] },
  },
  {
    slug: "scenic-walk-yarra-river-trail-melbourne",
    title: "Pleasant Riverside Walk on the Yarra River Trail, Melbourne",
    metaDescription:
      "Walk the Yarra River trail through Melbourne — a flat, leafy path along the water from Southbank past the Botanic Gardens, rowers and the city skyline.",
    intro:
      "The Yarra trail gives Melbourne a green spine — a flat, shaded riverside path from the heart of the city out past the Botanic Gardens, rowers gliding by on the water.",
    region: "Melbourne",
    mode: "walking",
    time: "1 hr",
    distance: "3 miles",
    pleasantness: "High",
    whyPleasant: [
      "Follows the Yarra River through the city",
      "Flat, separated, tree-lined path",
      "Passes the Royal Botanic Gardens",
      "Rowers and kayakers on the river",
      "The city skyline reflected in the water",
    ],
    whatYoullSee: [
      "The Southbank promenade and city skyline",
      "The Royal Botanic Gardens along the bank",
      "Rowing crews from the boathouses",
      "Herring Island in the middle of the river",
    ],
    bestTime: "A clear morning, or autumn when the introduced trees along the bank turn.",
    start: { name: "Southbank, Melbourne", coords: [144.9650, -37.8200] },
    end: { name: "Royal Botanic Gardens", coords: [144.9800, -37.8300] },
  },
];

/** One scenic hub per metro. Drives the hub pages, their App routes, the
 *  prerendered HTML, and the sitemap — add a metro here once and everything
 *  else (route /scenic-routes-<slug>, SEO, internal links) follows. `regions`
 *  is the set of RouteRegion values that roll up into this metro hub. */
export interface ScenicHub {
  /** URL path, e.g. "/scenic-routes-los-angeles". */
  path: string;
  title: string;
  description: string;
  heading: string;
  intro: string;
  regions: RouteRegion[];
}

export const SCENIC_HUBS: ScenicHub[] = [
  {
    path: "/scenic-routes-new-york",
    title: "Scenic & Pleasant Routes in New York and Westchester | Pleasant Routes",
    description:
      "Hand-picked scenic drives, pleasant routes and quiet walks in New York City and Westchester County — chosen for trees, water, low traffic and the way they feel.",
    heading: "Scenic & pleasant routes in New York and Westchester",
    intro:
      "A small, hand-picked set of the most pleasant routes in the area — scenic drives, quiet walks, and slow ways through the city. Chosen for trees, water, light traffic, and the way they feel.",
    regions: ["Westchester County", "New York City"],
  },
  {
    path: "/scenic-routes-san-francisco",
    title: "Scenic & Pleasant Routes in San Francisco & the Bay Area | Pleasant Routes",
    description:
      "Hand-picked scenic drives and quiet coastal walks in San Francisco and the Bay Area — the Pacific Coast Highway, the Marin Headlands, Lands End and more.",
    heading: "Scenic & pleasant routes in San Francisco and the Bay Area",
    intro:
      "A hand-picked set of the Bay Area's most pleasant routes — the coast highway, the Golden Gate, ridge drives and clifftop walks. Chosen for ocean, trees, light traffic, and the way they feel.",
    regions: ["San Francisco Bay Area"],
  },
  {
    path: "/scenic-routes-los-angeles",
    title: "Scenic & Pleasant Routes in Los Angeles | Pleasant Routes",
    description:
      "Hand-picked scenic drives in Los Angeles — the Pacific Coast Highway through Malibu, Mulholland Drive along the ridge, and the climb up Angeles Crest.",
    heading: "Scenic & pleasant routes in Los Angeles",
    intro:
      "Hand-picked LA drives that trade the freeway for the ocean and the ridgeline — Malibu's coast, Mulholland's overlooks, and the mountains above the city.",
    regions: ["Los Angeles"],
  },
  {
    path: "/scenic-routes-san-diego",
    title: "Scenic & Pleasant Routes in San Diego | Pleasant Routes",
    description:
      "Hand-picked coastal drives and walks in San Diego — the Highway 101 coast through La Jolla and Del Mar, Sunset Cliffs, and the Point Loma headland.",
    heading: "Scenic & pleasant routes in San Diego",
    intro:
      "Hand-picked San Diego routes that hug the Pacific — the old coast highway through the beach towns, the cliffs at sunset, and the view from Point Loma.",
    regions: ["San Diego"],
  },
  {
    path: "/scenic-routes-seattle",
    title: "Scenic & Pleasant Routes in Seattle | Pleasant Routes",
    description:
      "Hand-picked scenic drives and waterfront walks in Seattle — Lake Washington Boulevard, the cliffs of Chuckanut Drive, and the Alki Beach promenade.",
    heading: "Scenic & pleasant routes in Seattle",
    intro:
      "Hand-picked Seattle routes along the water and through the trees — lakeshore parkways, a cliffside coast drive, and a beach walk with the skyline across the Sound.",
    regions: ["Seattle"],
  },
  {
    path: "/scenic-routes-portland",
    title: "Scenic & Pleasant Routes in Portland, Oregon | Pleasant Routes",
    description:
      "Hand-picked scenic drives and walks near Portland — the Historic Columbia River Highway and its waterfalls, the wooded Skyline ridge, and the riverfront.",
    heading: "Scenic & pleasant routes in Portland",
    intro:
      "Hand-picked Portland routes into the green — the waterfall highway through the Columbia Gorge, a wooded ridge drive, and an easy walk along the Willamette.",
    regions: ["Portland"],
  },
  {
    path: "/scenic-routes-boston",
    title: "Scenic & Pleasant Routes in Boston | Pleasant Routes",
    description:
      "Hand-picked scenic routes in and around Boston — Memorial Drive along the Charles River, the Emerald Necklace park walk, and the Cape Ann coast.",
    heading: "Scenic & pleasant routes in Boston",
    intro:
      "Hand-picked Boston routes along the river and the shore — the Charles River parkways, Olmsted's chain of parks, and the rocky coast north of the city.",
    regions: ["Boston"],
  },
  {
    path: "/scenic-routes-chicago",
    title: "Scenic & Pleasant Routes in Chicago | Pleasant Routes",
    description:
      "Hand-picked scenic routes in Chicago — Lake Shore Drive along the lakefront, the Lakefront Trail, and Sheridan Road through the leafy North Shore.",
    heading: "Scenic & pleasant routes in Chicago",
    intro:
      "Hand-picked Chicago routes along Lake Michigan — the skyline drive on Lake Shore, the lakefront path, and the old estate road up the North Shore.",
    regions: ["Chicago"],
  },
  {
    path: "/scenic-routes-washington-dc",
    title: "Scenic & Pleasant Routes in Washington, DC | Pleasant Routes",
    description:
      "Hand-picked scenic routes in Washington, DC — Rock Creek Parkway, the George Washington Memorial Parkway along the Potomac, and the Tidal Basin walk.",
    heading: "Scenic & pleasant routes in Washington, DC",
    intro:
      "Hand-picked DC routes through the parks and along the Potomac — the wooded creek parkway, the riverside memorial parkway, and the cherry-tree walk around the Tidal Basin.",
    regions: ["Washington DC"],
  },
  {
    path: "/scenic-routes-denver",
    title: "Scenic & Pleasant Routes in Denver | Pleasant Routes",
    description:
      "Hand-picked scenic drives and trails near Denver — the Lariat Loop up Lookout Mountain, the Red Rocks foothills, and the Cherry Creek path through the city.",
    heading: "Scenic & pleasant routes in Denver",
    intro:
      "Hand-picked Denver routes into the foothills and along the water — the switchbacks up Lookout Mountain, the red-rock canyons, and a creekside trail through town.",
    regions: ["Denver"],
  },
  {
    path: "/scenic-routes-austin",
    title: "Scenic & Pleasant Routes in Austin | Pleasant Routes",
    description:
      "Hand-picked scenic routes in Austin — the Capital of Texas Highway (Loop 360) over the hills and bridges, and the Lady Bird Lake trail through the city.",
    heading: "Scenic & pleasant routes in Austin",
    intro:
      "Hand-picked Austin routes through the Hill Country edge — the 360 loop over its lake bridges and limestone hills, and the green trail that rings Lady Bird Lake.",
    regions: ["Austin"],
  },
  {
    path: "/scenic-routes-miami",
    title: "Scenic & Pleasant Routes in Miami | Pleasant Routes",
    description:
      "Hand-picked scenic drives in Miami — the A1A causeway and Ocean Drive, the Rickenbacker Causeway to Key Biscayne, and the banyan-arched Old Cutler Road.",
    heading: "Scenic & pleasant routes in Miami",
    intro:
      "Hand-picked Miami routes over the water and under the canopy — the beach causeways, the bridge out to Key Biscayne, and the old road tunneled through banyan trees.",
    regions: ["Miami"],
  },
  {
    path: "/scenic-routes-london",
    title: "Scenic & Pleasant Routes in London | Pleasant Routes",
    description:
      "Hand-picked scenic drives and quiet walks in London — Richmond Park and the Thames, the Royal Parks, and Hampstead Heath above the city.",
    heading: "Scenic & pleasant routes in London",
    intro:
      "Hand-picked London routes through the green and along the river — the deer park at Richmond, the chain of Royal Parks across the centre, and the wild heath above Hampstead.",
    regions: ["London"],
  },
  {
    path: "/scenic-routes-paris",
    title: "Scenic & Pleasant Routes in Paris | Pleasant Routes",
    description:
      "Hand-picked scenic walks and drives in Paris — the Seine quays, the leafy Bois de Boulogne, and the canal-side stroll along the Canal Saint-Martin.",
    heading: "Scenic & pleasant routes in Paris",
    intro:
      "Hand-picked Paris routes along the water and through the parks — the riverside quays of the Seine, the wooded Bois de Boulogne, and the locks of the Canal Saint-Martin.",
    regions: ["Paris"],
  },
  {
    path: "/scenic-routes-sydney",
    title: "Scenic & Pleasant Routes in Sydney | Pleasant Routes",
    description:
      "Hand-picked coastal walks and drives in Sydney — the Bondi to Coogee cliff walk, the Grand Pacific Drive over Sea Cliff Bridge, and the harbour at Manly.",
    heading: "Scenic & pleasant routes in Sydney",
    intro:
      "Hand-picked Sydney routes along the cliffs and the harbour — the famous Bondi-to-Coogee coast walk, the clifftop Grand Pacific Drive, and the headland path above Manly.",
    regions: ["Sydney"],
  },
  {
    path: "/scenic-routes-vancouver",
    title: "Scenic & Pleasant Routes in Vancouver | Pleasant Routes",
    description:
      "Hand-picked scenic routes in Vancouver — the Stanley Park Seawall, the Sea-to-Sky Highway toward Squamish, and Marine Drive along the West Vancouver shore.",
    heading: "Scenic & pleasant routes in Vancouver",
    intro:
      "Hand-picked Vancouver routes between the sea and the mountains — the Stanley Park seawall, the Sea-to-Sky coast highway, and the shoreline of West Vancouver.",
    regions: ["Vancouver"],
  },
  {
    path: "/scenic-routes-melbourne",
    title: "Scenic & Pleasant Routes in Melbourne | Pleasant Routes",
    description:
      "Hand-picked scenic routes near Melbourne — the world-famous Great Ocean Road along the coast, and the green Yarra River trail through the city.",
    heading: "Scenic & pleasant routes in Melbourne",
    intro:
      "Hand-picked Melbourne routes along the coast and the river — the legendary Great Ocean Road, and the shaded Yarra trail winding through the heart of the city.",
    regions: ["Melbourne"],
  },
];

/** The hub a given region rolls up into (for "more routes" cross-links). */
export const hubForRegion = (region: RouteRegion): ScenicHub =>
  SCENIC_HUBS.find((h) => h.regions.includes(region)) ?? SCENIC_HUBS[0];

export const getRouteBySlug = (slug: string): ScenicRoute | undefined =>
  SCENIC_ROUTES.find((r) => r.slug === slug);

/** Pick 3 related routes — same region first, then fill from the rest. */
export const getRelatedRoutes = (slug: string, n = 3): ScenicRoute[] => {
  const current = getRouteBySlug(slug);
  if (!current) return SCENIC_ROUTES.slice(0, n);
  const sameRegion = SCENIC_ROUTES.filter((r) => r.slug !== slug && r.region === current.region);
  const otherRegion = SCENIC_ROUTES.filter((r) => r.slug !== slug && r.region !== current.region);
  return [...sameRegion, ...otherRegion].slice(0, n);
};
